import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import {
    BehaviorSubject,
    Observable,
    catchError,
    filter,
    map,
    of,
    switchMap,
    take,
    tap,
    throwError,
} from 'rxjs';
import { environment } from 'src/environments/environment';
import { ApiResponse } from 'src/app/core/utils/api-response';
import { Staff } from 'src/app/core/staff/staff.types';
import { VendorCategoryCounts, VendorCounts } from './vendor.types';
import { VendorSharedService } from 'src/app/core/inventory/vendor/vendor.service';
import { Vendor } from 'src/app/core/inventory/vendor/vendor.types';

@Injectable({ providedIn: 'root' })
export class VendorService {
    // Private
    private API_URL = environment.apiUrl;



    /**
     * Constructor
     */
    constructor(private _httpClient: HttpClient,
        private _vendorSharedService: VendorSharedService
    ) { }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------




    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------
    // Vendor Methods
    getVendorsBySchoolCategory(schoolCode: string, categoryCode: string): Observable<ApiResponse<Vendor[]>> {
        return this._httpClient
            .get<ApiResponse<Vendor[]>>(`${this._vendorSharedService.getVendorsUrl}/school/${schoolCode}/category/${categoryCode}`)
            .pipe(
                tap((response) => {
                    if (response.success && response.data) {
                        this._vendorSharedService.updateVendors(response.data);
                    }
                }),
                catchError((error) => {
                    console.error('Failed to fetch vendors:', error);
                    const errorResponse: ApiResponse<Vendor[]> = error?.error || {
                        success: false,
                        message: 'Failed to fetch vendors',
                        data: null,
                        timestamp: new Date()
                    };
                    return throwError(() => errorResponse);
                })
            );
    }

    getVendorsPaged(
        schoolCode: string,
        page: number,
        size: number,
        search: string = '',
        sortField: string = '',
        sortOrder: number = 1,
        filters: { [key: string]: any } = {}
    ): Observable<ApiResponse<{ content: Vendor[]; totalElements: number }>> {
        let url = `${this._vendorSharedService.getVendorsUrl}/school/${schoolCode}/paged?page=${page}&size=${size}`;
        if (search) url += `&search=${encodeURIComponent(search)}`;
        if (sortField) url += `&sort=${sortField},${sortOrder === 1 ? 'asc' : 'desc'}`;

        // Add filters as query params (array or object)
        Object.keys(filters).forEach(key => {
            const value = filters[key];
            if (Array.isArray(value)) {
                value.forEach(v => url += `&${key}=${encodeURIComponent(v)}`);
            } else if (value !== undefined && value !== null && value !== '') {
                url += `&${key}=${encodeURIComponent(value)}`;
            }
        });

        return this._httpClient.get<ApiResponse<{ content: Vendor[]; totalElements: number }>>(url)
        .pipe(
                    tap((response) => {
                        if (response.success && response.data && Array.isArray(response.data.content)) {
                            this._vendorSharedService.updateVendors(response.data.content);
                        }
                    }),
                    catchError((error) => {
                        // ...error handling...
                        return throwError(() => error);
                    })
                );
    }

    getVendorCounts(): Observable<ApiResponse<VendorCounts>> {
        return this._vendorSharedService.categories$.pipe(
            take(1),
            map(categories =>{
                        // Sum vendorCount from each category (default to 0 if missing)
                        const totalVendors = (categories || []).reduce(
                            (sum, category) => sum + (category.vendorCount || 0),
                            0
                        );
                        const counts: VendorCounts = {
                            totalVendors: totalVendors || 0,
                            activeVendors:  0,
                            inactiveVendors:  0,
                            totalCategories: categories?.length || 0
                        };
                        return {
                            success: true,
                            data: counts,
                            message: 'Vendor counts retrieved successfully',
                            timestamp: new Date()
                        };
            })
        );
    }

    getVendorCategoryCounts(): Observable<ApiResponse<VendorCategoryCounts>> {
        return this._vendorSharedService.categories$.pipe(
            take(1),
            map(categories => {
                // Sum vendorCount from each category (default to 0 if missing)
                const totalVendors = (categories || []).reduce(
                    (sum, category) => sum + (category.vendorCount || 0),
                    0
                );
                const counts: VendorCategoryCounts = {
                    totalVendors,
                    totalCategories: categories?.length || 0,

                };
                return {
                    success: true,
                    data: counts,
                    message: 'Vendor category counts retrieved successfully',
                    timestamp: new Date()
                };
            })
        );
    }

    /**
     * Export vendors as a file (e.g., Excel)
     */
    exportVendors(schoolCode: string): Observable<Blob> {
        // Replace with your actual backend export endpoint
        return this._httpClient.get(`${this.API_URL}/vendors/${schoolCode}/export`, {
            responseType: 'blob'
        });
    }

}
